// Generic client for the old ERP backend (apierp.azurewebsites.net), replacing Supabase.
// See docs/API_MIGRATION_MAPPING.md and docs/BACKEND_SPEC_MISSING_ENDPOINTS.md.
//
// IMPORTANT re: auth failures on this backend — most of this API (including
// the CRM module) reports auth/permission failures as a 200 OK response with
// a JSON envelope ({hasErrors: true, errors: "...", errorCode: "..."}), not
// as an HTTP 401/403 status. So a call can fail authentication and still
// pass the `res.ok` check below — always check `envelope.hasErrors` too, not
// just the HTTP status. describeHttpError() below covers the (rarer) case
// where the backend does fail at the HTTP layer.

/** Turns an HTTP failure status into a specific, human-readable category instead of a bare number. */
function describeHttpError(status: number, statusText: string): string {
  switch (status) {
    case 401: return `401 Unauthorized — the API credential was rejected (${statusText})`;
    case 403: return `403 Forbidden — the API credential lacks permission for this request (${statusText})`;
    case 404: return `404 Not Found — the endpoint path or route does not exist (${statusText})`;
    case 405: return `405 Method Not Allowed — this endpoint requires a different HTTP method (${statusText})`;
    case 422: return `422 Unprocessable Entity — the request payload was rejected as invalid (${statusText})`;
    default:
      if (status >= 500) return `${status} Server Error — the backend failed to process this request (${statusText})`;
      return `${status} ${statusText}`;
  }
}

// In-flight request de-duplication for READ calls only (getTemplateDataList /
// getCrmTemplateDataList) — if the exact same templateID+filters+pagination
// is already loading, every caller gets the SAME pending promise instead of
// firing a second identical network request. This only collapses calls that
// are genuinely concurrent (both start before either resolves); once a
// request settles its key is removed, so the next poll tick or a later
// on-demand refetch still goes out as a real, fresh request — this is
// deliberately NOT a cache, and does not change how often live/polling data
// refreshes. Never applied to writes (addUpdateTemplateDataWeb,
// deleteTemplateData, etc.) — those must never be silently collapsed.
const inFlightReads = new Map<string, Promise<unknown>>();

async function dedupeInFlight<T>(key: string, run: () => Promise<T>): Promise<T> {
  const pending = inFlightReads.get(key);
  if (pending) return pending as Promise<T>;

  const promise = run().finally(() => {
    inFlightReads.delete(key);
  });
  inFlightReads.set(key, promise);
  return promise;
}

/** Wraps fetch() so a network/DNS/CORS-level failure (fetch throws, never reaches an HTTP response) is reported distinctly from an HTTP error status. */
async function fetchOrThrow(url: string, init: Parameters<typeof fetch>[1], context: string): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch (err) {
    // A TypeError here (no HTTP status at all) is what a browser CORS block,
    // DNS failure, or dropped connection all look like — fetch() gives no
    // way to tell these apart, so name the category, not a false-precise cause.
    throw new Error(
      `Network or CORS error — the request to ${context} never reached the server (check connectivity, or that the server allows cross-origin requests from this origin). Underlying: ${err instanceof Error ? err.message : String(err)}`
    );
  }
}

// Trim defensively — a stray trailing newline/space from how a hosting
// platform injects env vars would otherwise silently corrupt every request
// (e.g. `Authorization-Token: <token>\n` sent as a literal header value).
// Does not alter the token content itself, only edge whitespace.
function readEnvVar(value: string | undefined): string {
  return (value ?? "").trim();
}

const BASE_URL = readEnvVar(import.meta.env.VITE_API_BASE_URL);
const AUTH_TOKEN = readEnvVar(import.meta.env.VITE_API_AUTH_TOKEN); // header: Authorization-Token
const AUTH_KEY = readEnvVar(import.meta.env.VITE_API_AUTH_KEY); // header: authkey (dynamicApp/*)

// Separate CRM module on the same backend host, with its own Authorization-Token
// (the generic AUTH_TOKEN above returns "Unauthorized User"/E001 on this route —
// confirmed live 2026-08-19). Unlike /api/getTemplateDataList, this route does NOT
// reject reads for templateID 4 (Students) — see getCrmTemplateDataList below.
// Never falls back to AUTH_TOKEN/AUTH_KEY if this is missing — a wrong-but-
// present credential on this route fails as E001 ("Unauthorized User"), which
// is harder to diagnose than this app's own "not configured" message below.
const CRM_ORIGIN = BASE_URL.replace(/\/api$/, "");
const CRM_AUTH_TOKEN = readEnvVar(import.meta.env.VITE_API_CRM_AUTH_TOKEN);
const CRM_TMP_UID = 40330;
const CRM_TMP_PID = 40330;

export function isApiConnected(): boolean {
  return !!BASE_URL && !!AUTH_TOKEN && !!AUTH_KEY;
}

export function isCrmApiConnected(): boolean {
  return !!CRM_ORIGIN && !!CRM_AUTH_TOKEN;
}

// Fail loudly in dev (not in production — a missing var there should degrade
// to this app's existing isApiConnected()-gated error states, not a console
// wall on every page) if critical config is absent, so a typo'd or missing
// .env entry is obvious immediately rather than surfacing later as a vague
// failed request. Never logs the token values themselves.
if (import.meta.env.DEV) {
  const missing: string[] = [];
  if (!BASE_URL) missing.push("VITE_API_BASE_URL");
  if (!AUTH_TOKEN) missing.push("VITE_API_AUTH_TOKEN");
  if (!AUTH_KEY) missing.push("VITE_API_AUTH_KEY");
  if (!CRM_AUTH_TOKEN) missing.push("VITE_API_CRM_AUTH_TOKEN");
  if (missing.length > 0) {
    console.warn(
      `[apiClient] Missing environment variable(s): ${missing.join(", ")}. ` +
      `Copy .env.example to .env and fill these in — requests depending on them will fail until then.`
    );
  }
}

export interface ApiEnvelope<T = unknown> {
  hasErrors: boolean;
  errors: string;
  response: T;
  objRootDetails?: unknown;
  /** e.g. "E003" for an expired Authorization-Token — see isExpiredCredentialError() below. */
  errorCode?: string | null;
}

// This backend reports an expired/invalid credential as a 200 OK response
// with hasErrors: true, errorCode: "E003", errors: "Authorization-Token
// Expired" — not an HTTP 401. Recognize it explicitly (by code first, falling
// back to the message text in case a different endpoint on this backend ever
// omits errorCode) so callers can tell "the credential is expired" apart from
// every other failure mode and say so plainly, instead of a generic
// "request failed" that reads like a bug in this app's request formatting.
const EXPIRED_TOKEN_ERROR_CODE = "E003";

export function isExpiredCredentialError(envelope: Pick<ApiEnvelope, "hasErrors" | "errors" | "errorCode">): boolean {
  if (!envelope.hasErrors) return false;
  if (envelope.errorCode === EXPIRED_TOKEN_ERROR_CODE) return true;
  return /authorization-token\s+expired/i.test(envelope.errors || "");
}

/** Human-readable, non-alarmist message for an expired-credential envelope — names which env var to rotate, doesn't imply a code bug. */
export function describeExpiredCredentialError(envelope: Pick<ApiEnvelope, "errorCode" | "errors">): string {
  return `ERP credential expired (${envelope.errorCode || EXPIRED_TOKEN_ERROR_CODE}: ${envelope.errors}). The request itself is correct — this requires a newly issued Authorization-Token from the ERP administrator, set as VITE_API_CRM_AUTH_TOKEN in .env.`;
}

async function request<T>(path: string, body: unknown, headerType: "authToken" | "authKey"): Promise<ApiEnvelope<T>> {
  const headers: Record<string, string> = {
    Accept: "application/json, text/plain",
    "Content-Type": "application/json;charset=UTF-8",
  };
  headers[headerType === "authToken" ? "Authorization-Token" : "authkey"] = headerType === "authToken" ? AUTH_TOKEN : AUTH_KEY;

  const res = await fetchOrThrow(`${BASE_URL}${path}`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  }, path);

  if (!res.ok) {
    throw new Error(`API request failed: ${describeHttpError(res.status, res.statusText)} (${path})`);
  }

  try {
    return await (res.json() as Promise<ApiEnvelope<T>>);
  } catch {
    throw new Error(`Invalid API response — ${path} returned a 200 OK but the body was not valid JSON.`);
  }
}

// --- Core generic operations (existing on the old backend) ---

export interface TemplateRow {
  value: string | number | null;
}

export interface TemplateListResponse {
  records: TemplateRow[][];
  // The live backend returns the row count as `totalRecords`, not `count` —
  // `count` is kept as a fallback alias in case a future endpoint uses it.
  totalRecords?: number;
  count?: number;
}

export async function getTemplateDataList(
  templateID: number,
  filters: Record<string, unknown> = {},
  opts: { index?: number; pgSize?: number; numberOfFieldsView?: number } = {},
): Promise<ApiEnvelope<TemplateListResponse>> {
  // NOTE: this old-app endpoint lives at /api/api/getTemplateDataList on the
  // live backend (a doubled path segment, confirmed directly from the old
  // app's source — every getTemplateDataList/addUpdateTemplateDataWeb/
  // UploadCommonFilesToAzure call there is a hardcoded ".../api/api/..."
  // URL). The /dynamicApp/* endpoints below do NOT double up.
  //
  // Some templates (confirmed: Students=4, Admins=7 — anything holding
  // login credentials) reject ALL reads through this endpoint regardless of
  // filters, responding 200 with `response: null` and a soft
  // "Failed! Unauthorized user" error rather than an HTTP error — this looks
  // like a deliberate backend restriction (credential data isn't listable
  // via the generic content API), not a transient failure. Normalize that
  // (and any other null-response case) to an empty result here so every
  // caller downstream gets a safe "not found" instead of crashing on
  // `response.records` being read off `null`.
  const requestBody = {
    NumberOfFieldsView: opts.numberOfFieldsView ?? 50,
    filters,
    isDownload: false,
    index: opts.index ?? 1,
    pgSize: opts.pgSize ?? 100,
    templateID,
  };
  const dedupeKey = `getTemplateDataList:${JSON.stringify(requestBody)}`;
  const envelope = await dedupeInFlight(dedupeKey, () =>
    request<TemplateListResponse>("/api/getTemplateDataList", requestBody, "authToken")
  );

  if (!envelope.response) {
    return { ...envelope, response: { records: [], totalRecords: 0 } };
  }
  return envelope;
}

export function addUpdateTemplateDataWeb(templateID: number, fields: Record<string, unknown>, id?: string) {
  const body: Record<string, unknown> = { templateID: String(templateID), ...fields };
  if (id) body._id = id;
  return request<unknown>("/api/addUpdateTemplateDataWeb", body, "authToken");
}

// Confirmed live 2026-09-28 (Invoicenew.js's fun_Login): unlike
// /api/getTemplateDataList, this endpoint does NOT reject reads for
// templateID 4 (Students) — it returns named-cell rows (fields labeled by
// name, not positional), same shape as the CRM module's response. This is
// the one live-working path to read an ARBITRARY student's full profile
// (not just the currently-logged-in one) without a CRM token.
export interface DynamicTemplateListResponse {
  index: number;
  pgSize: number;
  totalRecords: number;
  fields: string[] | null;
  records: CrmNamedCell[][];
}

export function getDynamicTemplateList(
  typeName: string,
  filters: Record<string, unknown> = {},
  opts: { index?: number; pgSize?: number; numberOfFieldsView?: number } = {},
) {
  return request<DynamicTemplateListResponse>("/dynamicApp/getDynamicTemplateList", {
    NumberOfFieldsView: opts.numberOfFieldsView ?? 50,
    filters,
    isDownload: false,
    index: opts.index ?? 1,
    pgSize: opts.pgSize ?? 100,
    TypeName: typeName,
  }, "authKey");
}

/** Reads a named cell by label, tolerant of the backend's inconsistent trailing-tab field names (e.g. "HeightinCM\t"). */
export function namedCell(row: CrmNamedCell[], label: string): string {
  const target = label.trim().toLowerCase();
  const found = row.find((c) => c.name.trim().toLowerCase() === target);
  return found?.value != null ? String(found.value) : "";
}

export function getMaxRecordidInTemplate(templateID: number) {
  return request<{ maxid: number }>("/dynamicApp/getMaxRecordidInTemplate", { templateID }, "authKey");
}

export function checkLoginVerify(email: string, password: string, role?: number) {
  const body: Record<string, unknown> = { EmailID: email, password };
  if (role !== undefined) body.Role = role;
  return request<TemplateRow[][]>("/dynamicApp/checkLoginVerify", body, "authKey");
}

/**
 * @param onProgress optional 0–100 upload-progress callback. fetch() can't
 *   report upload progress, so passing one switches to XMLHttpRequest; the
 *   request and response handling are otherwise identical.
 */
export async function uploadFile(file: File, onProgress?: (percent: number) => void): Promise<string> {
  const form = new FormData();
  form.append("myFile", file);
  form.append("name", file.name);
  const url = `${BASE_URL}/api/UploadCommonFilesToAzure`;

  let data: { hasErrors?: boolean; errors?: string; response?: { imageUrl?: string }[] };
  if (onProgress) {
    data = await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", url);
      xhr.setRequestHeader("Authorization-Token", AUTH_TOKEN);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = () => {
        if (xhr.status < 200 || xhr.status >= 300) {
          reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
          return;
        }
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch {
          reject(new Error("Upload failed: the server's response was not valid JSON."));
        }
      };
      xhr.onerror = () => reject(new Error("Upload failed: network error. Check your connection and try again."));
      xhr.ontimeout = () => reject(new Error("Upload timed out. Please try again."));
      xhr.onabort = () => reject(new Error("Upload was interrupted. Please try again."));
      xhr.send(form);
    });
  } else {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Authorization-Token": AUTH_TOKEN },
      body: form,
    });
    if (!res.ok) throw new Error(`Upload failed: ${res.status} ${res.statusText}`);
    data = await res.json();
  }
  // This endpoint rejects some files with hasErrors: true and a specific
  // reason in `errors` (e.g. "Failed! Provide atleast 250px width or
  // height." for undersized images) while still returning HTTP 200 — confirmed
  // live. Checking res.ok alone isn't enough; a rejection here was
  // previously swallowed into a generic, misleading "Upload succeeded but no
  // file URL was returned" message that hid the real reason from the user.
  if (data?.hasErrors) {
    throw new Error(data.errors || "Upload was rejected by the server.");
  }
  const fileUrl = data?.response?.[0]?.imageUrl;
  if (!fileUrl) throw new Error("Upload succeeded but no file URL was returned");
  return fileUrl as string;
}

// --- HTML→PDF / email (old app's Invoicenew.js — confirmed live 2026-09-28) ---
// downloadHTMlToPDF (note: "HTMl", not "Html" — matches the old app's exact,
// misspelled route name) renders arbitrary HTML server-side and returns a
// real, publicly reachable blob URL (dialerpstorageac.blob.core.windows.net)
// — this is the one to use for download/print.
// downloadHTMlToPDFForVouchers was ALSO tested live the same way and returns
// a local server filesystem path (e.g. "C:\home\site\wwwroot\...") instead of
// a URL — not usable from the browser. Confirmed broken for our purposes;
// don't use it even though the old app's "Send to Email" flow does.

export function downloadHtmlToPdf(fileName: string, htmlData: string) {
  return request<string>("/api/downloadHTMlToPDF", { userID: 40330, fileName, htmlData }, "authToken");
}

export function getEmailID(searchFromEmail: string) {
  return request<{ fromEmail: string; fromName: string; smtpHost: string; smtpPort: number; smtpPassword: string; smtpAuthDomain: string }>(
    "/api/GetEmailID",
    { ParentId: 40330, userID: 40330, searchFromEmail },
    "authToken",
  );
}

export interface SendEmailWithAttachmentInput {
  toEmailID: string;
  attachment: string;
  subject: string;
  body: string;
  contactName: string;
  toEmail: string;
  fileName: string;
  smtpHost: string;
  smtpPort: number;
  smtpPassword: string;
  fromEmail: string;
  fromName: string;
  smtpAuthDomain: string;
}

export function sendEmailWithAttachment(input: SendEmailWithAttachmentInput) {
  return request<unknown>("/api/SendEmailwithAttachment", {
    index: 1,
    userID: 40330,
    toEmailID: input.toEmailID,
    attachment: input.attachment,
    Subject: input.subject,
    Body: input.body,
    items: [{ accountName: "InDirect Dddd Lead", contactName: input.contactName, email: input.toEmail, userID: 40330, isChecked: true }],
    smtpHost: input.smtpHost,
    smtpPort: input.smtpPort,
    smtpPassword: input.smtpPassword,
    fromEmail: input.fromEmail,
    fromName: input.fromName,
    smtpAuthDomain: input.smtpAuthDomain,
    toEmailIDs: input.toEmailID,
    fileName: input.fileName,
  }, "authToken");
}

// --- New operations (per docs/BACKEND_SPEC_MISSING_ENDPOINTS.md) ---
// These call endpoints that may not exist on the backend yet. They will fail
// until the backend team implements the P0/P1/P2 spec — that is expected
// during the migration window, not a bug in this client.

export function deleteTemplateData(templateID: number, ids: string[]) {
  return request<{ deletedCount: number }>("/deleteTemplateData", { templateID, ids }, "authToken");
}

export function adjustSeats(templateID: number, rowId: string, delta: number) {
  return request<{ availableSeats: number }>("/adjustSeats", { templateID, rowId, delta }, "authToken");
}

export function bulkImportTemplateData(
  templateID: number,
  rows: Record<string, unknown>[],
  opts: { onConflictField?: string; upsert?: boolean } = {},
) {
  return request<{ insertedCount: number; updatedCount: number; skippedCount: number }>(
    "/bulkImportTemplateData",
    { templateID, rows, ...opts },
    "authToken",
  );
}

// Confirmed 404 live (2026-08-20) — never implemented. No longer called
// anywhere; batchService.fetchBookingCounts now derives booked seats by
// reading Bookings (templateID 5) directly instead (see
// seatAvailabilityService.ts). Kept here, unused, as a record of what was
// tried — remove once the backend team confirms this route will never exist.
export function getBatchBookingCounts(courseCodes: string[]) {
  return request<Record<string, number>>(`/batches/bookingCounts?courseCodes=${courseCodes.join(",")}`, {}, "authToken");
}

// --- CRM module (separate auth, separate host path) ---
// Confirmed live 2026-08-19 against POST https://apierp.azurewebsites.net
// /api/CRM/GetTemplateDataList?appname=web&tmpUId=40330&tmpPId=40330 (GET on
// this path 404s — the route only responds to POST). Each record is an array
// of named cells ({name, value}), not the positional array the rest of this
// client works with, and critically this route does NOT apply the
// Students(4)/Admins(7) read-block that /api/getTemplateDataList has — it's
// currently the only confirmed-working way to list templateID 4 rows.

export interface CrmNamedCell {
  name: string;
  value: string | number | null;
}

export interface CrmTemplateListResponse {
  index: number;
  pgSize: number;
  totalRecords: number;
  fields: string[] | null;
  records: CrmNamedCell[][];
}

export async function getCrmTemplateDataList(
  templateID: number,
  opts: { index?: number; pgSize?: number; numberOfFieldsView?: number; filters?: Record<string, unknown> } = {},
): Promise<ApiEnvelope<CrmTemplateListResponse>> {
  const dedupeKey = `getCrmTemplateDataList:${templateID}:${JSON.stringify(opts)}`;
  return dedupeInFlight(dedupeKey, () => getCrmTemplateDataListUncached(templateID, opts));
}

async function getCrmTemplateDataListUncached(
  templateID: number,
  opts: { index?: number; pgSize?: number; numberOfFieldsView?: number; filters?: Record<string, unknown> },
): Promise<ApiEnvelope<CrmTemplateListResponse>> {
  if (!isCrmApiConnected()) {
    return { hasErrors: true, errors: "CRM API not configured (missing VITE_API_CRM_AUTH_TOKEN)", response: { index: 0, pgSize: 0, totalRecords: 0, fields: null, records: [] } };
  }

  const url = `${CRM_ORIGIN}/api/CRM/GetTemplateDataList?appname=web&tmpUId=${CRM_TMP_UID}&tmpPId=${CRM_TMP_PID}`;
  const res = await fetchOrThrow(url, {
    method: "POST",
    headers: {
      Accept: "application/json, text/plain",
      "Content-Type": "application/json;charset=UTF-8",
      "Authorization-Token": CRM_AUTH_TOKEN,
    },
    body: JSON.stringify({
      templateID,
      index: opts.index ?? 1,
      pgSize: opts.pgSize ?? 100,
      NumberOfFieldsView: opts.numberOfFieldsView ?? 50,
      isDownload: false,
      filters: opts.filters ?? {},
    }),
  }, "/api/CRM/GetTemplateDataList");

  if (!res.ok) {
    throw new Error(`CRM API request failed: ${describeHttpError(res.status, res.statusText)} (/api/CRM/GetTemplateDataList)`);
  }

  let envelope: ApiEnvelope<CrmTemplateListResponse>;
  try {
    envelope = await res.json();
  } catch {
    throw new Error("Invalid API response — /api/CRM/GetTemplateDataList returned a 200 OK but the body was not valid JSON.");
  }
  if (import.meta.env.DEV) {
    console.log(`[CRM getTemplateDataList] templateID=${templateID} raw response:`, envelope);
  }
  if (!envelope.response) {
    return { ...envelope, response: { index: 0, pgSize: 0, totalRecords: 0, fields: null, records: [] } };
  }
  return envelope;
}

// --- Helpers for the positional-array record shape ---

export function cell(row: TemplateRow[], index: number): string {
  return row[index]?.value != null ? String(row[index].value) : "";
}

export function rowsToObjects<T extends Record<string, number>>(
  records: TemplateRow[][],
  schema: T,
): Record<keyof T, string>[] {
  return records.map((row) => {
    const obj = {} as Record<keyof T, string>;
    for (const key in schema) {
      obj[key] = cell(row, schema[key]);
    }
    return obj;
  });
}
